import * as Yup from 'yup';
import capitalize from 'lodash/capitalize';
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Icon, IconWrapper, Input, Link, Optional } from '@honda-canada/design-system-react';
import { Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SectionTitleWithTooltip from '../shared/SectionTitleWithTooltip';
import gtmTradeInOwing from '../../../core/gtm/gtmTradeInOwing';
import parseFormattedPrice from '../../../utils/parseFormattedPrice';
import themeStyles from './TradeInOptions.styles';
import useBapRegisterUserInteraction from '../../../utils/hooks/bap/useBapRegisterUserInteraction';
import { CalculatorFields, selectIsPaymentMethodCash, selectPaymentOptions, updateCalculatorField, } from '../../../core/reducers/financialCalculators';
import { EqualHeight } from '../../../utils/components/EqualHeight';
import { useAppSelector } from '../../../core/utils';
import { useFetchCurrentVehicleFinancialCalculatorPayment } from '../../../core/apiHooks/useFetchPayment';
import { useLanguage } from '../../../utils/sitecoreContext';
const Wrapper = themeStyles.apply(Box, 'Wrapper');
const MainContainer = themeStyles.apply(Box, 'MainContainer');
const InputWrapper = themeStyles.apply(Box, 'InputWrapper');
const StyledInput = themeStyles.apply(Input, 'StyledInput');
const ButtonWrapper = themeStyles.apply(Box, 'ButtonWrapper');
const TradeInOwingButton = themeStyles.apply(Link, 'TradeInOwingButton');
const TradeInOwingIcon = themeStyles.apply(IconWrapper, 'TradeInOwingIcon');
const MAX_LENGTH = 10;
const TradeInOptions = ({ calculatorId, disableAllFields, onFieldChange, }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const language = useLanguage();
    const [errors, setErrors] = useState({});
    const [currentInput, setCurrentInput] = useState(null);
    const { registerUserInteraction } = useBapRegisterUserInteraction('paymentCalculator');
    const { data, isFetching } = useFetchCurrentVehicleFinancialCalculatorPayment(calculatorId);
    const { downPaymentAmount, tradeInValueAmount, tradeInOwingAmount, paymentMethod } = useAppSelector(state => selectPaymentOptions(state, calculatorId));
    const [showTradeInOwing, setShowTradeInOwing] = useState(!!tradeInOwingAmount);
    const isPaymentMethodCash = useAppSelector(state => selectIsPaymentMethodCash(state, calculatorId));
    const isTradeInOwingVisible = showTradeInOwing || !!tradeInOwingAmount;
    const maxDownPaymentValue = (data === null || data === void 0 ? void 0 : data.downPaymentAmount) || 0;
    const maxTradeInValue = (data === null || data === void 0 ? void 0 : data.tradeInValueAmount) || 0;
    const maxTradeInOwingValue = (data === null || data === void 0 ? void 0 : data.tradeInOwingAmount) || 0;
    const validateFields = useCallback(async () => {
        const validationSchema = Yup.object().shape({
            [CalculatorFields.DownPaymentAmount]: Yup.number()
                .max(maxDownPaymentValue, t(`Pages.Bap.exceedDownPayment${capitalize(paymentMethod)}Error`))
                .nullable(),
            [CalculatorFields.TradeInValueAmount]: Yup.number()
                .max(maxTradeInValue, t(`Pages.Bap.exceedTradeIn${capitalize(paymentMethod)}Error`))
                .nullable(),
            [CalculatorFields.TradeInOwingAmount]: Yup.number().nullable(),
        });
        try {
            await validationSchema.validate({
                downPaymentAmount,
                tradeInValueAmount,
                tradeInOwingAmount,
            });
            setErrors({});
        }
        catch (err) {
            if (err instanceof Yup.ValidationError) {
                // @ts-ignore
                setErrors(prevErrors => ({ ...prevErrors, [err.path]: err.message }));
                dispatch(updateCalculatorField({
                    id: calculatorId,
                    field: { [CalculatorFields.DownPaymentAmount]: maxDownPaymentValue },
                }));
                dispatch(updateCalculatorField({
                    id: calculatorId,
                    field: { [CalculatorFields.TradeInValueAmount]: maxTradeInValue },
                }));
                if (onFieldChange) {
                    onFieldChange();
                }
                if (downPaymentAmount !== 0 && currentInput === CalculatorFields.TradeInValueAmount) {
                    setErrors(prevErrors => ({
                        ...prevErrors,
                        downPaymentAmount: t(`Pages.Bap.exceedDownPayment${capitalize(paymentMethod)}Error`),
                    }));
                }
            }
        }
    }, [
        maxDownPaymentValue,
        t,
        paymentMethod,
        maxTradeInValue,
        downPaymentAmount,
        tradeInValueAmount,
        tradeInOwingAmount,
        dispatch,
        calculatorId,
        onFieldChange,
        currentInput,
    ]);
    useEffect(() => {
        const shouldValidateDownPayment = downPaymentAmount !== maxDownPaymentValue;
        const shouldValidateTradeInValue = tradeInValueAmount !== maxTradeInValue;
        if (!isFetching && (shouldValidateDownPayment || shouldValidateTradeInValue)) {
            validateFields();
        }
    }, [
        validateFields,
        maxDownPaymentValue,
        maxTradeInValue,
        isFetching,
        downPaymentAmount,
        tradeInValueAmount,
        currentInput,
    ]);
    useEffect(() => {
        setErrors({});
        dispatch(updateCalculatorField({
            id: calculatorId,
            field: {
                [CalculatorFields.TradeInOwingAmount]: maxTradeInOwingValue,
                [CalculatorFields.DownPaymentAmount]: maxDownPaymentValue,
                [CalculatorFields.TradeInValueAmount]: maxTradeInValue,
            },
        }));
    }, [paymentMethod, maxTradeInOwingValue, maxDownPaymentValue, maxTradeInValue, calculatorId, dispatch]);
    const handleInputChange = (e, setFieldValue) => {
        const { name, value } = e.target;
        // Remove all non-numeric characters except decimal point
        const numericValue = value.replace(/[^0-9.]/g, '');
        // Count the number of decimal points
        const decimalCount = (numericValue.match(/\./g) || []).length;
        // Allow only one decimal point and update value
        if (decimalCount <= 1) {
            if (numericValue.length <= MAX_LENGTH) {
                setFieldValue(name, numericValue);
            }
            else {
                // If length exceeds MAX_LENGTH, truncate the value
                setFieldValue(name, numericValue.substring(0, MAX_LENGTH));
            }
            setErrors({});
        }
    };
    const handleBlur = (e, setFieldValue) => {
        const { name, value } = e.target;
        setCurrentInput(name);
        const numericValue = parseFormattedPrice(value, language) || 0;
        const shouldUpdateTradeInValue = (name === CalculatorFields.TradeInValueAmount && numericValue === 0 && tradeInOwingAmount > 0) ||
            (name === CalculatorFields.TradeInOwingAmount && numericValue > 0 && tradeInValueAmount === 0);
        const updateField = (fieldName, fieldValue) => {
            dispatch(updateCalculatorField({ id: calculatorId, field: { [fieldName]: fieldValue } }));
            setFieldValue(fieldName, formatPrice(fieldValue || '', language, 2));
            registerUserInteraction();
        };
        updateField(name, numericValue);
        gtmTradeInOwing(numericValue);
        if (shouldUpdateTradeInValue) {
            updateField(CalculatorFields.TradeInValueAmount, 1);
        }
        if (onFieldChange) {
            onFieldChange();
        }
    };
    const handleTradeInOwingButtonClick = () => {
        if (tradeInOwingAmount)
            return;
        setShowTradeInOwing(prev => !prev);
        dispatch(updateCalculatorField({ id: calculatorId, field: { [CalculatorFields.TradeInOwingAmount]: 0 } }));
    };
    const handleOnKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
        }
    };
    return (React.createElement(Wrapper, { "data-testid": "cy-payment-calc-trade-in-options" },
        React.createElement(Formik, { initialValues: {
                [CalculatorFields.DownPaymentAmount]: downPaymentAmount ? formatPrice(downPaymentAmount, language, 2) : '',
                [CalculatorFields.TradeInValueAmount]: tradeInValueAmount ? formatPrice(tradeInValueAmount, language, 2) : '',
                [CalculatorFields.TradeInOwingAmount]: tradeInOwingAmount ? formatPrice(tradeInOwingAmount, language, 2) : '',
            }, enableReinitialize: true, onSubmit: () => { } }, ({ setFieldValue }) => (React.createElement(Form, null,
            React.createElement(MainContainer, null,
                React.createElement(EqualHeight, null,
                    React.createElement(Optional, { when: !isPaymentMethodCash },
                        React.createElement(InputWrapper, null,
                            React.createElement(SectionTitleWithTooltip, { title: t('Pages.Bap.downPaymentLabel'), tooltip: t('Pages.Bap.downPaymentTooltipText'), dataTestId: "cy-payment-calc-down-payment" }),
                            React.createElement(Box, null,
                                React.createElement(Field, { as: StyledInput, name: CalculatorFields.DownPaymentAmount, ariaLabel: "downPaymentAmount", placeholder: t('Pages.Bap.zeroDollarPlaceholder'), onChange: (e) => handleInputChange(e, setFieldValue), onBlur: (e) => handleBlur(e, setFieldValue), disabled: disableAllFields, hasError: !!errors[CalculatorFields.DownPaymentAmount], errorMessage: errors[CalculatorFields.DownPaymentAmount], onKeyDown: handleOnKeyDown })))),
                    React.createElement(InputWrapper, null,
                        React.createElement(SectionTitleWithTooltip, { title: t('Pages.Bap.tradeInLabel'), tooltip: t('Pages.Bap.tradeInTooltipText'), dataTestId: "cy-payment-calc-trade-in-value" }),
                        React.createElement(Field, { as: StyledInput, name: CalculatorFields.TradeInValueAmount, ariaLabel: "tradeInValueAmount", placeholder: t('Pages.Bap.zeroDollarPlaceholder'), onChange: (e) => handleInputChange(e, setFieldValue), onBlur: (e) => handleBlur(e, setFieldValue), disabled: disableAllFields, hasError: !!errors[CalculatorFields.TradeInValueAmount], errorMessage: errors[CalculatorFields.TradeInValueAmount], onKeyDown: handleOnKeyDown })))),
            React.createElement(Optional, { when: !isPaymentMethodCash },
                React.createElement(ButtonWrapper, null,
                    React.createElement(TradeInOwingButton, { href: true, styling: "tertiary2", onClick: handleTradeInOwingButtonClick, "data-testid": "cy-trade-in-owing-button", disabled: tradeInOwingAmount },
                        isTradeInOwingVisible
                            ? t('Pages.Bap.removeTradeInOwingButton')
                            : t('Pages.Bap.addTradeInOwingButton'),
                        React.createElement(TradeInOwingIcon, null,
                            React.createElement(Icon, { name: "animatedPlusMinus", iconColor: tradeInOwingAmount ? 'disabled' : 'blue', toggle: isTradeInOwingVisible })))),
                React.createElement(Optional, { when: isTradeInOwingVisible },
                    React.createElement(SectionTitleWithTooltip, { title: t('Pages.Bap.tradeInOwingLabel'), tooltip: t('Pages.Bap.tradeInOwingTooltipText'), dataTestId: "cy-payment-calc-trade-in-owing" }),
                    React.createElement(Field, { as: StyledInput, name: CalculatorFields.TradeInOwingAmount, ariaLabel: "tradeInOwingAmount", placeholder: t('Pages.Bap.zeroDollarPlaceholder'), onChange: (e) => handleInputChange(e, setFieldValue), onBlur: (e) => handleBlur(e, setFieldValue), disabled: disableAllFields, hasError: !!errors[CalculatorFields.TradeInOwingAmount], errorMessage: errors[CalculatorFields.TradeInOwingAmount], onKeyDown: handleOnKeyDown }))))))));
};
export default TradeInOptions;
